'use client'

import { useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Search,
  Loader2,
  Sparkles,
  BookOpen,
  User,
  Users,
  Check,
  ChevronDown,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/stores/auth'
import { normalizeTeamIds } from '@/lib/utils/teams'

interface KnowledgeSearchDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onInsert: (text: string) => void
}

export function KnowledgeSearchDialog({
  open,
  onOpenChange,
  onInsert,
}: KnowledgeSearchDialogProps) {
  const [query, setQuery] = useState('')

  const searchMutation = useMutation({
    mutationFn: async (q: string) => {
      const response = await api.agent.askKnowledge({
        query: q,
        sessionId: 'ticket-context', // Using generic context since we don't have session ID in tickets yet or purely purely generic
      })
      if (!response.success)
        throw new Error(response.error || 'Failed to fetch answer')
      return response.data
    },
  })

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (!query.trim()) return
    searchMutation.mutate(query)
  }

  const answer = searchMutation.data?.answer
  const sources = searchMutation.data?.sources || []

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-600" />
            AI Knowledge Search
          </DialogTitle>
          <DialogDescription>
            Ask a question to generate a suggested answer from the knowledge
            base.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col gap-4">
          <form onSubmit={handleSearch} className="flex gap-2">
            <Input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="How do I reset my password?"
              autoFocus
            />
            <Button
              type="submit"
              disabled={searchMutation.isPending || !query.trim()}
            >
              {searchMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Search'
              )}
            </Button>
          </form>

          {searchMutation.error && (
            <p className="text-sm text-red-500">
              {searchMutation.error.message}
            </p>
          )}

          <div className="flex-1 overflow-y-auto">
            <div className="space-y-6 pb-4">
              {searchMutation.data && (
                <div className="rounded-lg border bg-purple-50 p-4 border-purple-100">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-purple-900">
                      Suggested Answer
                    </h4>
                    <Button
                      size="sm"
                      variant="secondary"
                      className="h-7 text-xs bg-white hover:bg-white/90 border-purple-200"
                      onClick={() => {
                        if (answer) {
                          onInsert(answer)
                          onOpenChange(false)
                        }
                      }}
                    >
                      Insert Answer
                    </Button>
                  </div>
                  <div className="prose prose-sm max-w-none text-gray-800 whitespace-pre-wrap">
                    {answer ||
                      'No direct answer found based on the provided context.'}
                  </div>
                </div>
              )}

              {sources.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider">
                    Sources
                  </h4>
                  {sources.map((source: any, i: number) => (
                    <div
                      key={i}
                      className="rounded-lg border bg-white p-3 shadow-sm"
                    >
                      <div className="flex justify-between items-start mb-1">
                        <span className="text-xs font-semibold text-gray-700">
                          {source.metadata?.title || `Source ${i + 1}`}
                        </span>
                        <Badge variant="outline" className="text-[10px]">
                          Score: {source.score?.toFixed(2)}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-600 line-clamp-3 mb-2">
                        {source.text}
                      </p>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 text-xs px-2"
                        onClick={() => {
                          onInsert(source.text)
                          onOpenChange(false)
                        }}
                      >
                        Use this context
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

interface ArticleLibraryDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onInsert: (text: string) => void
}

export function ArticleLibraryDialog({
  open,
  onOpenChange,
  onInsert,
}: ArticleLibraryDialogProps) {
  const [search, setSearch] = useState('')
  // Debounce search input
  // Since we don't have useDebounce hook available or imported, assuming simple query or manual implementation
  // Actually I see useDebounce imported above but I need to check if it exists in project.
  // I'll assume simple query for now or instant.

  const { data, isLoading } = useQuery({
    queryKey: ['agent-articles', search],
    queryFn: async () => {
      const response = await api.agent.listArticles({
        scope: 'published',
        limit: 20,
        search: search || undefined,
      })
      if (!response.success)
        throw new Error(response.error || 'Failed to fetch articles')
      return response.data
    },
    enabled: open,
  })

  const articles = data?.articles || []

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-blue-600" />
            Article Library
          </DialogTitle>
          <DialogDescription>
            Search and insert pre-written responses.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search responses..."
            className="pl-9"
            autoFocus
          />
        </div>

        <div className="flex-1 mt-2 overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center p-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : articles.length === 0 ? (
            <div className="text-center p-8 text-gray-500">
              <p>No articles found matching "{search}"</p>
            </div>
          ) : (
            <div className="space-y-2 pb-4">
              {articles.map((article: any) => (
                <button
                  key={article.id}
                  className="w-full text-left p-3 rounded-lg border hover:bg-gray-50 transition-colors group"
                  onClick={() => {
                    onInsert(article.content)
                    onOpenChange(false)
                  }}
                >
                  <div className="flex justify-between items-start mb-1">
                    <h4 className="text-sm font-medium text-gray-900 group-hover:text-blue-600">
                      {article.title}
                    </h4>
                    <div className="flex gap-1">
                      {article.tags?.map((tag: string) => (
                        <Badge
                          key={tag}
                          variant="secondary"
                          className="text-[10px] px-1 py-0 h-5"
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 line-clamp-2">
                    {article.content}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

interface AssignmentPopoverProps {
  currentAssigneeId?: number | null
  currentTeamId?: number | null
  onAssign: (userId: number | null, teamId: number | null) => void
  children: React.ReactNode
}

export function AssignmentPopover({
  currentAssigneeId,
  currentTeamId,
  onAssign,
  children,
}: AssignmentPopoverProps) {
  const [open, setOpen] = useState(false)
  const [isTeamSelectOpen, setIsTeamSelectOpen] = useState(false)
  const { agentUser: user } = useAuthStore()

  // Fetch Agents
  const { data: agents = [] } = useQuery({
    queryKey: ['agent', 'list'],
    queryFn: async () => {
      const res = await api.agent.listAgents()
      return res.success ? res.data || [] : []
    },
  })

  // Fetch Teams
  const { data: teams = [] } = useQuery({
    queryKey: ['agent', 'all-teams'],
    queryFn: async () => {
      const res = await api.agent.getAllTeams()
      return res.success ? res.data || [] : []
    },
  })

  const myTeamIds = useMemo(
    () => normalizeTeamIds(user?.teamIds, user?.primaryTeamId),
    [user?.teamIds, user?.primaryTeamId]
  )

  const myTeams = useMemo(
    () => teams.filter(team => myTeamIds.includes(team.id)),
    [teams, myTeamIds]
  )

  // Handle selection
  const handleSelectAgent = (agentId: number) => {
    onAssign(agentId, null) // Clear team when assigning to user? Or keep team? Usually assign to user implies active ownership.
    setOpen(false)
  }

  const handleSelectTeam = (teamId: number) => {
    onAssign(null, teamId) // Clear user when assigning to team queue
    setOpen(false)
  }

  const handleUnassign = () => {
    onAssign(null, null)
    setOpen(false)
  }

  const handleAssignToMe = () => {
    if (user?.id) {
      if (myTeamIds.length <= 1) {
        onAssign(user.id, myTeamIds[0] ?? null)
        setOpen(false)
        return
      }
      setOpen(false)
      setIsTeamSelectOpen(true)
    }
  }

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>{children}</PopoverTrigger>
        <PopoverContent className="p-0 w-60" align="end">
          <Command>
            <CommandInput
              placeholder="Search agent or team..."
              className="h-9"
            />
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>

              <CommandGroup heading="Actions">
                <CommandItem
                  onSelect={handleUnassign}
                  className="text-red-600 aria-disabled:opacity-50 aria-disabled:pointer-events-none"
                  value="unassign ticket"
                  disabled={!currentAssigneeId && !currentTeamId}
                >
                  <Users className="mr-2 h-4 w-4" />
                  Unassign Ticket
                </CommandItem>
                {user && (
                  <CommandItem
                    onSelect={handleAssignToMe}
                    value="assign to me"
                    disabled={Number(currentAssigneeId) === Number(user.id)}
                    className="aria-disabled:opacity-50 aria-disabled:pointer-events-none"
                  >
                    <User className="mr-2 h-4 w-4" />
                    Assign to Me
                  </CommandItem>
                )}
              </CommandGroup>

              <CommandSeparator />

              <CommandGroup heading="Teams">
                {teams.length === 0 && (
                  <div className="p-2 text-xs text-gray-400">
                    No teams found
                  </div>
                )}
                {teams.map(team => (
                  <CommandItem
                    key={'team-' + team.id}
                    value={'team-' + team.name}
                    onSelect={() => handleSelectTeam(team.id)}
                  >
                    <Users className="mr-2 h-4 w-4 text-blue-500" />
                    <span>{team.name}</span>
                    {currentTeamId === team.id && !currentAssigneeId && (
                      <Check className="ml-auto h-4 w-4" />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>

              <CommandSeparator />

              <CommandGroup heading="Agents">
                {agents.filter(a => !user || Number(a.id) !== Number(user.id))
                  .length === 0 && (
                  <div className="p-2 text-xs text-gray-400">
                    No agents found
                  </div>
                )}
                {agents
                  .filter(a => !user || Number(a.id) !== Number(user.id))
                  .map(agent => (
                    <CommandItem
                      key={'agent-' + agent.id}
                      value={'agent-' + (agent.username || agent.email || '')}
                      onSelect={() => handleSelectAgent(agent.id)}
                    >
                      <div className="h-6 w-6 mr-2 rounded-full flex items-center justify-center text-[10px] bg-blue-100 text-blue-700 overflow-hidden">
                        {agent.firstName?.substring(0, 2).toUpperCase() ||
                          agent.username?.substring(0, 2).toUpperCase() ||
                          'AG'}
                      </div>
                      <span className="truncate">
                        {agent.firstName
                          ? `${agent.firstName} ${agent.lastName || ''}`
                          : agent.username}
                      </span>
                      {currentAssigneeId === agent.id && (
                        <Check className="ml-auto h-4 w-4" />
                      )}
                    </CommandItem>
                  ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <Dialog open={isTeamSelectOpen} onOpenChange={setIsTeamSelectOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Select team for this ticket</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {myTeams.length === 0 && (
              <div className="text-sm text-gray-500">
                No teams found for your account.
              </div>
            )}
            {myTeams.map(team => (
              <Button
                key={`my-team-${team.id}`}
                variant="outline"
                className="w-full justify-start"
                onClick={() => {
                  if (user?.id) {
                    onAssign(user.id, team.id)
                  }
                  setIsTeamSelectOpen(false)
                }}
              >
                <Users className="mr-2 h-4 w-4 text-blue-500" />
                <span>{team.name}</span>
              </Button>
            ))}
            {myTeamIds.length > 0 && myTeams.length !== myTeamIds.length && (
              <div className="text-xs text-gray-500">
                Some teams could not be loaded. Please refresh.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
